import Keycloak, {KeycloakInitOptions} from 'keycloak-js';
import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {useKeycloakConfig} from '../config/config-hooks';
import {HibuKeycloakConfig} from '../config/config-types';
import AuthService from './AuthService';
import {Spinner} from '../common/spinner/spinner';
import {getRandomUserId} from '../settings/duck/settings-localstorage-service';

const promiseResolver: any[] = [];
const globalAuthService: Promise<AuthService> = new Promise((resolve, reject) => {
    promiseResolver.push(resolve);
});

/**
 * KeycloakInitOptions configures the Keycloak client.
 */
const keycloakInitOptions: KeycloakInitOptions = {
    onLoad: 'login-required',
    checkLoginIframe: false, // Important: Disable iframe polling, needed to prevent page reloads
    redirectUri: window.location.href,
};

/**
 * AuthContextValues defines the structure for the default values of the {@link AuthContext}.
 */
interface AuthContextValues {
    /**
     * AuthService that provides the needed methods
     */
    authService: AuthService | undefined | null;
    /**
     * The first name of the authenticated user
     */
    firstName: string;
    /**
     * The last name of the authenticated user
     */
    lastName: string;
    /**
     * The id of the authenticated user
     */
    userId: string | null;
}

/**
 * Default values for the {@link AuthContext}
 */
const defaultAuthContextValues: AuthContextValues = {
    authService: undefined,
    firstName: '',
    lastName: '',
    userId: null,
};

/**
 * Create the AuthContext using the default values.
 */
export const AuthContext = createContext<AuthContextValues>(
    defaultAuthContextValues
);

/**
 * The props that must be passed to create the {@link AuthContextProvider}.
 */
interface AuthContextProviderProps {
    /**
     * The elements wrapped by the auth context.
     */
    children: JSX.Element;
}

/**
 * AuthContextProvider is responsible for managing the authentication state of the current user.
 *
 * @param props
 */
const AuthContextProvider = (props: AuthContextProviderProps) => {
    // KeycloakConfig configures the connection to the Keycloak server
    const hibuKeycloakConfig: HibuKeycloakConfig = useKeycloakConfig();
    const randomUserId = getRandomUserId();
    const enabled: boolean = hibuKeycloakConfig.enabled;

    // Create the Keycloak client instance
    const keycloak = useMemo(() => (hibuKeycloakConfig.enabled ? new Keycloak(hibuKeycloakConfig) : null), [hibuKeycloakConfig]);

    // Create the local state in which we will keep track if a user is authenticated
    const [authService, setAuthService] = useState<AuthService | null>(null);

    // Local state that will contain the users data once it is loaded
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [userId, setUserId] = useState<string>(randomUserId);

    function setStateUnauthenticated(randomUserId: string) {
        setFirstName('N');
        setLastName('A');
        setUserId(randomUserId);
    }

    // Dynamically initialize Keycloak and AuthService when config is loaded
    useEffect(() => {
        async function loadProfile(keycloak: Keycloak): Promise<string> {
            try {
                const profile = await keycloak.loadUserProfile();
                if (profile.firstName) {
                    setFirstName(profile.firstName);
                }
                if (profile.lastName) {
                    setLastName(profile.lastName);
                }
                if (profile.id) {
                    setUserId(profile.id);
                    return profile.id;
                } else {
                    console.warn('No user id from profile received.');
                }
            } catch {
                console.log('Error trying to load the users profile');
            }
            return Promise.resolve(randomUserId);
        }

        async function initialize() {
            if (enabled && keycloak) {
                if (!keycloak.didInitialize) {
                    const initialized = await keycloak.init(keycloakInitOptions);
                    if (!initialized) {
                        await keycloak.login();
                    } else {
                        const userIdFromProfile = await loadProfile(keycloak);
                        const newAuthService = new AuthService(true, userIdFromProfile, keycloak);
                        setAuthService(newAuthService);
                        promiseResolver[0](newAuthService); // Set global instance
                        promiseResolver.pop();
                    }
                }
            } else {
                const disabledAuthService = new AuthService(false, randomUserId, undefined);
                setAuthService(disabledAuthService);
                promiseResolver[0](disabledAuthService);
                promiseResolver.pop();
                setStateUnauthenticated(randomUserId);
            }
        }

        initialize();
    }, [enabled, keycloak, randomUserId]);

    // Set up the context provider
    return (
        <AuthContext.Provider
            value={{authService, firstName, lastName, userId}}
        >
            {authService ? props.children : <Spinner/>}
        </AuthContext.Provider>
    );
};

// Custom hook to use the number format context
export const useAuthContext = () => {
    return useContext(AuthContext);
};

// Export the global instance for fetcher
export const getGlobalAuthService = () => globalAuthService;

export default AuthContextProvider;
